/**
 * AI Error Handler Utility
 * 
 * Provides centralized error handling for AI service requests across all workshops.
 * Displays user-friendly error messages instead of technical errors.
 * 
 * @module aiErrorHandler
 */

/**
 * User-friendly error messages for different AI error scenarios
 */
const ERROR_MESSAGES = {
  // Service unavailable / Rate limiting
  SERVICE_UNAVAILABLE: {
    title: 'Service AI temporairement indisponible',
    titleEn: 'AI Service Temporarily Unavailable',
    message: 'Le service de suggestions IA est momentanément indisponible. Vous pouvez continuer en saisissant les informations manuellement. Veuillez contacter ACG pour assistance.',
    messageEn: 'The AI suggestion service is temporarily unavailable. You can continue by entering information manually. Please contact ACG for assistance.',
    type: 'warning'
  },
  
  // Network errors
  NETWORK_ERROR: {
    title: 'Problème de connexion',
    titleEn: 'Connection Issue',
    message: 'Impossible de se connecter au service IA. Vérifiez votre connexion internet ou réessayez plus tard. Veuillez contacter ACG pour assistance.',
    messageEn: 'Unable to connect to the AI service. Check your internet connection or try again later. Please contact ACG for assistance.',
    type: 'warning'
  },
  
  // Timeout errors
  TIMEOUT_ERROR: {
    title: 'Délai d\'attente dépassé',
    titleEn: 'Request Timeout',
    message: 'La génération des suggestions prend plus de temps que prévu. Veuillez réessayer ou saisir manuellement. Veuillez contacter ACG pour assistance.',
    messageEn: 'Generating suggestions is taking longer than expected. Please try again or enter manually. Please contact ACG for assistance.',
    type: 'warning'
  },
  
  // Invalid response format
  INVALID_RESPONSE: {
    title: 'Réponse invalide',
    titleEn: 'Invalid Response',
    message: 'Le service IA a retourné une réponse inattendue. Veuillez réessayer ou saisir les informations manuellement. Veuillez contacter ACG pour assistance.',
    messageEn: 'The AI service returned an unexpected response. Please try again or enter information manually. Please contact ACG for assistance.',
    type: 'warning'
  },
  
  // Generic AI error
  GENERIC_ERROR: {
    title: 'Service IA indisponible',
    titleEn: 'AI Service Unavailable',
    message: 'Le service de suggestions IA n\'est pas disponible pour le moment. Vous pouvez continuer en saisissant les informations manuellement. Veuillez contacter ACG pour assistance.',
    messageEn: 'The AI suggestion service is not available at the moment. You can continue by entering information manually. Please contact ACG for assistance.',
    type: 'warning'
  },
  
  // Authentication/Authorization errors
  AUTH_ERROR: {
    title: 'Accès refusé',
    titleEn: 'Access Denied',
    message: 'Vous n\'avez pas les permissions nécessaires pour utiliser le service IA. Veuillez contacter ACG pour assistance.',
    messageEn: 'You do not have the necessary permissions to use the AI service. Please contact ACG for assistance.',
    type: 'error'
  }
};

/**
 * Detects the type of error based on the error object
 * @param {Error} error - The error object
 * @returns {string} Error type key
 */
const detectErrorType = (error) => {
  if (!error) return 'GENERIC_ERROR';
  
  const message = (error.message || '').toLowerCase();
  const status = error.response?.status;
  
  // Check for specific HTTP status codes
  if (status === 429 || status === 503 || status === 502) {
    return 'SERVICE_UNAVAILABLE';
  }
  
  if (status === 401 || status === 403) {
    return 'AUTH_ERROR';
  }
  
  if (status === 408 || message.includes('timeout') || message.includes('etimedout')) {
    return 'TIMEOUT_ERROR';
  }
  
  // Check for network errors
  if (message.includes('network') || 
      message.includes('connexion') || 
      message.includes('connection') ||
      message.includes('enotfound') ||
      message.includes('econnrefused') ||
      !navigator.onLine) {
    return 'NETWORK_ERROR';
  }
  
  // Check for invalid data format
  if (message.includes('invalid data format') || 
      message.includes('json') ||
      message.includes('parse')) {
    return 'INVALID_RESPONSE';
  }
  
  // Check for AI service specific errors
  if (message.includes('ai service') ||
      message.includes('gemini') ||
      message.includes('ollama') ||
      message.includes('structured output') ||
      message.includes('invalid json') ||
      message.includes('temporarily unavailable') ||
      message.includes('service unavailable') ||
      message.includes('overload') ||
      message.includes('quota') ||
      message.includes('timed out') ||
      message.includes('failed to generate') ||
      message.includes('failed to get') ||
      message.includes('failed to suggest')) {
    return 'SERVICE_UNAVAILABLE';
  }
  
  return 'GENERIC_ERROR';
};

/**
 * Gets the appropriate error message based on error type and language
 * @param {string} errorType - The type of error
 * @param {string} language - Language code ('fr' or 'en')
 * @returns {Object} Error message object with title, message, and type
 */
const getErrorMessage = (errorType, language = 'fr') => {
  const errorConfig = ERROR_MESSAGES[errorType] || ERROR_MESSAGES.GENERIC_ERROR;
  
  return {
    title: language === 'en' ? errorConfig.titleEn : errorConfig.title,
    message: language === 'en' ? errorConfig.messageEn : errorConfig.message,
    type: errorConfig.type
  };
};

/**
 * Shows a user-friendly notification for AI errors
 * @param {Object} options - Notification options
 * @param {string} options.title - Notification title
 * @param {string} options.message - Notification message
 * @param {string} options.type - Notification type (error, warning, info, success)
 * @param {number} options.duration - Duration in milliseconds
 * @param {Array} options.actions - Optional action buttons
 */
const showErrorNotification = (options) => {
  const { title, message, type = 'warning', duration = 8000, actions = [] } = options;
  
  // Use the global notification system if available
  if (typeof window !== 'undefined' && window.showNotification) {
    window.showNotification({
      title,
      message,
      type,
      duration,
      actions
    });
  } else {
    // Fallback to console for development
    console.warn(`[AI Error] ${title}: ${message}`);
    
    // Fallback to alert in production if notification system not available
    if (process.env.NODE_ENV === 'production') {
      // eslint-disable-next-line no-alert
      alert(`${title}\n\n${message}`);
    }
  }
};

/**
 * Main error handler function for AI service calls
 * @param {Error} error - The error object from the failed request
 * @param {Object} options - Handler options
 * @param {string} options.language - Language code ('fr' or 'en')
 * @param {boolean} options.showNotification - Whether to show notification (default: true)
 * @param {Function} options.onError - Optional callback to execute on error
 * @param {boolean} options.logError - Whether to log error to console (default: true)
 * @returns {Object} Standardized error response
 */
export const handleAIError = (error, options = {}) => {
  const {
    language = 'fr',
    showNotification: shouldShowNotification = true,
    onError = null,
    logError = true
  } = options;
  
  // Detect error type
  const errorType = detectErrorType(error);
  
  // Get user-friendly message
  const errorMessage = getErrorMessage(errorType, language);
  
  // Log error for debugging (but hide technical details from users)
  if (logError) {
    console.error('[AI Service Error]', {
      type: errorType,
      originalError: error.message,
      status: error.response?.status,
      timestamp: new Date().toISOString()
    });
  }
  
  // Show user-friendly notification
  if (shouldShowNotification) {
    showErrorNotification({
      title: errorMessage.title,
      message: errorMessage.message,
      type: errorMessage.type,
      duration: 8000,
      actions: [
        {
          label: language === 'en' ? 'Close' : 'Fermer',
          action: 'dismiss'
        }
      ]
    });
  }
  
  // Execute optional callback
  if (onError && typeof onError === 'function') {
    onError(error, errorType, errorMessage);
  }
  
  // Return standardized error response
  return {
    success: false,
    error: {
      type: errorType,
      userMessage: errorMessage,
      technicalMessage: error?.message || 'Unknown error'
    }
  };
};

/**
 * Wraps an async AI service call with error handling
 * @param {Function} asyncFn - The async function to wrap
 * @param {Object} options - Handler options (same as handleAIError)
 * @returns {Promise} Result of the async function or standardized error response
 */
export const withAIErrorHandling = async (asyncFn, options = {}) => {
  try {
    const result = await asyncFn();
    return result;
  } catch (error) {
    return handleAIError(error, options);
  }
};

/**
 * Creates a wrapped version of a service function with AI error handling
 * @param {Function} serviceFn - The service function to wrap
 * @param {Object} defaultOptions - Default error handling options
 * @returns {Function} Wrapped service function
 */
export const createAIErrorHandledService = (serviceFn, defaultOptions = {}) => {
  return async (...args) => {
    try {
      return await serviceFn(...args);
    } catch (error) {
      return handleAIError(error, defaultOptions);
    }
  };
};

/**
 * HOC for wrapping React components with AI error handling
 * Provides a handleAIError method via props
 */
export const withAIErrorHandler = (Component) => {
  return function WrappedComponent(props) {
    const handleError = (error, options = {}) => {
      return handleAIError(error, {
        language: props.language || 'fr',
        ...options
      });
    };
    
    return <Component {...props} handleAIError={handleError} />;
  };
};

export default {
  handleAIError,
  withAIErrorHandling,
  createAIErrorHandledService,
  withAIErrorHandler,
  ERROR_MESSAGES
};